import { Box, Paper, Typography, useTheme } from '@mui/material'
import { SmokingRooms as SmokingIcon, Timer as TimerIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useShowMiniSessionBar } from '@hooks/useShowMiniSessionBar'
import { useActiveSmokingSession } from '@hooks/useActiveSmokingSession'
import { useSmokingSessionRuntime } from '@/contexts/SmokingSessionRuntimeContext'
import { useShowBottomNav } from '@hooks/useShowBottomNav'
import { MINI_SESSION_BAR_HEIGHT } from '@components/layout/layoutConstants'
import { shellFixedBottomOffsetCss, shellOverlayZIndex } from '@components/layout/shellOverlayStack'
import { useShellOverlayMetrics } from '@components/layout/shellOverlayMetrics'
import { formatSmokingTimer } from '@utils/smokingSessionState'
import { trackFeature } from '@utils/trackFeature'

export function MiniSessionBar() {
  const show = useShowMiniSessionBar()
  const { activeSession } = useActiveSmokingSession()
  const { elapsedSeconds, paused } = useSmokingSessionRuntime()
  const showBottomNav = useShowBottomNav()
  const { keyboardInsetPx } = useShellOverlayMetrics()
  const theme = useTheme()
  const navigate = useNavigate()

  if (!show || !activeSession) {
    return null
  }

  const cigarLabel = `${activeSession.cigar.cigarLine.brand.name} ${activeSession.cigar.cigarLine.name}`
  const bottomOffset = shellFixedBottomOffsetCss(
    { bottomNav: showBottomNav, miniSessionBar: false },
    { keyboardInsetPx },
  )

  return (
    <Paper
      component="button"
      type="button"
      elevation={6}
      aria-label={`Live session ${cigarLabel}, ${paused ? 'paused, ' : ''}${formatSmokingTimer(elapsedSeconds)}. Open live session`}
      onClick={() => {
        trackFeature('cta_smoke_start', { source: 'mini_bar' })
        navigate('/app/journal')
      }}
      data-testid="mini-session-bar"
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: bottomOffset,
        zIndex: shellOverlayZIndex(theme).miniSessionBar,
        height: MINI_SESSION_BAR_HEIGHT,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 0,
        appearance: 'none',
        WebkitAppearance: 'none',
        font: 'inherit',
        border: 'none',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        cursor: 'pointer',
        textAlign: 'left',
        borderRadius: 0,
      }}
    >
      <SmokingIcon fontSize="small" aria-hidden />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight="bold" noWrap>
          {cigarLabel}
        </Typography>
        <Typography variant="caption" color="inherit" sx={{ opacity: 0.9 }}>
          {paused ? 'Paused · ' : ''}Tap to open live session
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
        <TimerIcon fontSize="small" aria-hidden />
        <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
          {formatSmokingTimer(elapsedSeconds)}
        </Typography>
      </Box>
    </Paper>
  )
}

export default MiniSessionBar
